import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Grid,
  Paper,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  IconButton,
  Chip,
} from "@mui/material";
import {
  Search as SearchIcon,
  MoreVert as MoreIcon,
  Visibility as ViewIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";
import { useSnackbar } from "notistack";
import axios from "axios";
import Loader from "@jumbo/components/Loader";
import { useDispatch, useSelector } from "react-redux";
import { usePagination } from "app/hooks/usePagination";
import { getMeeshoOrderPaymentsList } from "app/redux/slice/meeshoOrderManagementSlice";
import { Table as CustomTable } from "app/components";
import moment from "moment";
import { LocalizationProvider, DateRangePicker } from "@mui/lab";
import AdapterDateFns from "@mui/lab/AdapterDateFns";

const PaymentList = () => {
  const dispatch = useDispatch();
  const meeshoData = useSelector((state) => state?.meeshoOrderManagement);
  const orders = meeshoData?.payments?.list || [];
  const [dateRange, setDateRange] = useState([null, null]); // active filters used for fetch
  const [orderDateRange, setOrderDateRange] = useState([null, null]); // active order date filters

  // TEMP states for inputs (user edits these, then clicks Apply)
  const [tempDateRange, setTempDateRange] = useState([null, null]);
  const [tempOrderDateRange, setTempOrderDateRange] = useState([null, null]);

  const { enqueueSnackbar } = useSnackbar();
  const [searchTerm, setSearchTerm] = useState(""); // actual search used for fetch
  const [tempSearchTerm, setTempSearchTerm] = useState(""); // input search

  const [filters, setFilters] = useState({
    liveOrderStatus: "",
    priceType: "",
    paymentStatus: "",
  }); // actual filters used for fetch
  const [tempFilters, setTempFilters] = useState({
    liveOrderStatus: "",
    priceType: "",
    paymentStatus: "",
  }); // input filters

  const [selectedOrders, setSelectedOrders] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [statusDialog, setStatusDialog] = useState(false);
  const [newStatus, setNewStatus] = useState("");
  const [statusReason, setStatusReason] = useState("");
  const [detailsDialog, setDetailsDialog] = useState(false);
  const [orderDetails, setOrderDetails] = useState(null);
  const [order, setOrder] = useState("desc");
  const [orderBy, setOrderBy] = useState("createdAt");

  const { page, rowsPerPage, handleChangePage, handleChangeRowsPerPage } =
    usePagination({ id: "meesho-management-orders" });

  const statusOptions = [
    { value: "Delivered", label: "Delivered", color: "success" },
    { value: "Return", label: "Return", color: "error" },
    { value: "Shipped", label: "Shipped", color: "info" },
    { value: "RTO", label: "RTO", color: "warning" },
    { value: "Exchange", label: "Exchange", color: "info" },
  ];

  // fetch whenever active filter/search/pagination/sort change
  useEffect(() => {
    fetchOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    page,
    rowsPerPage,
    dispatch,
    searchTerm,
    order,
    orderBy,
    filters,
    dateRange,
    orderDateRange,
  ]);

  const handleDateChange = (newDateRange) => {
    setTempDateRange(newDateRange); // change temp only
  };

  const handleOrderDateChange = (newDateRange) => {
    setTempOrderDateRange(newDateRange); // change temp only
  };

  const fetchOrders = async () => {
    const params = {
      page: page,
      perPage: rowsPerPage,
      search: searchTerm,
      ...filters,
    };
    if (dateRange[0] !== null && dateRange[1] !== null) {
      params.startDate = moment(dateRange[0]).format("YYYY-MM-DD");
      params.endDate = moment(dateRange[1]).format("YYYY-MM-DD");
    }
    if (orderDateRange[0] !== null && orderDateRange[1] !== null) {
      params.orderFromDate = moment(orderDateRange[0]).format("YYYY-MM-DD");
      params.orderToDate = moment(orderDateRange[1]).format("YYYY-MM-DD");
    }

    dispatch(getMeeshoOrderPaymentsList(params));
  };

  const applyFilters = () => {
    // copy temp inputs to active filters and search, trigger page reset
    setFilters({ ...tempFilters });
    setSearchTerm(tempSearchTerm);
    setDateRange(tempDateRange);
    setOrderDateRange(tempOrderDateRange);
    handleChangePage(0, 0);
  };

  const clearAll = () => {
    setTempDateRange([null, null]);
    setTempOrderDateRange([null, null]);
    setDateRange([null, null]);
    setOrderDateRange([null, null]);

    setTempSearchTerm("");
    setSearchTerm("");

    const cleared = {
      liveOrderStatus: "",
      priceType: "",
      paymentStatus: "",
    };
    setTempFilters(cleared);
    setFilters(cleared);

    handleChangePage(0, 0);
  };

  const columns = [
    {
      id: "subOrderNo",
      label: "Sub Order No",
      sort: false,
    },
    {
      id: "orderDate",
      label: "Order Date",
      sort: false,
      render: (row) => <>{moment.utc(row?.orderDate).format("DD/MM/YYYY")}</>,
    },
    {
      id: "paymentDate",
      label: "Payment Date",
      sort: false,
      render: (row) => <>{moment.utc(row?.paymentDate).format("DD/MM/YYYY")}</>,
    },
    {
      id: "productName",
      label: "Product Name",
      sort: false,
      render: (row) => (
        <Typography variant="body1" noWrap sx={{ maxWidth: 150 }}>
          {row?.productName}
        </Typography>
      ),
    },
    {
      id: "supplierSku",
      label: "SKU",
      sort: false,
    },
    {
      id: "status",
      label: "Status",
      sort: false,
      render: (row) => (
        <>
          {(() => {
            const status = statusOptions.find(
              (s) =>
                s.value.toLowerCase() ===
                (row?.liveOrderStatus || "").toLowerCase()
            );

            return (
              <Chip
                label={status?.label || "N/A"}
                color={status?.color || "default"}
                size="small"
                variant={status ? "filled" : "outlined"}
              />
            );
          })()}
        </>
      ),
    },
    {
      id: "priceType",
      label: "Price Type",
      sort: false,
      render: (row) => <>{row?.priceType}</>,
    },
    {
      id: "listingPrice",
      label: "Listing Price",
      isTotal: true,
      sort: false,
      align: "right",
      format: (value) => formatCurrency(value),
    },
    {
      id: "totalSaleAmount",
      label: "Sale Amount",
      sort: false,
      isTotal: true,
      align: "right",
      format: (value) => formatCurrency(value),
    },
    {
      id: "returnShippingCharge",
      label: "Return Charges",
      sort: false,
      isTotal: true,
      align: "right",
      format: (value) => formatCurrency(value),
    },
    {
      id: "finalSettlementAmount",
      label: "Final Settlement",
      sort: false,
      isTotal: true,
      align: "right",
      format: (value) => formatCurrency(value),
    },
    {
      id: "actions",
      label: "Actions",
      align: "right",
      minWidth: 50,
      render: (row) => (
        <IconButton
          onClick={(e) => {
            setAnchorEl(e.currentTarget);
            setSelectedOrder(row);
          }}
        >
          <MoreIcon />
        </IconButton>
      ),
    },
  ];

  const sortData = (Order, OrderBy) => {
    setOrder(Order);
    setOrderBy(OrderBy);
  };

  const handleSearchInputChange = (event) => {
    setTempSearchTerm(event.target.value);
  };

  const handleTempFilterChange = (field, value) => {
    setTempFilters((prev) => ({ ...prev, [field]: value }));
  };

  const handleBulkStatusUpdate = async () => {
    if (selectedOrders.length === 0) {
      enqueueSnackbar("Please select orders to update", { variant: "warning" });
      return;
    }

    try {
      const response = await axios.put("/meesho/orders/bulk-status", {
        orderIds: selectedOrders,
        status: newStatus,
        reason: statusReason,
      });

      if (response.data.statusCode === 200) {
        enqueueSnackbar(
          `Updated ${response.data.data.updated} orders successfully`,
          { variant: "success" }
        );
        setSelectedOrders([]);
        setStatusDialog(false);
        fetchOrders();
      }
    } catch (error) {
      console.error("Bulk update error:", error);
      enqueueSnackbar("Failed to update orders", { variant: "error" });
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
    }).format(amount);
  };

  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom>
        Payments List
      </Typography>
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={5}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search orders..."
                value={tempSearchTerm}
                onChange={handleSearchInputChange}
                InputProps={{
                  startAdornment: (
                    <SearchIcon sx={{ mr: 1, color: "text.secondary" }} />
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={3.5}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DateRangePicker
                  startText="Payment Start Date - End Date"
                  endText="To Date"
                  inputFormat="dd/MM/yyyy"
                  value={tempDateRange}
                  onChange={handleDateChange}
                  renderInput={({ inputProps, ...startProps }, endProps) => {
                    const startValue = inputProps.value;
                    delete inputProps.value;
                    return (
                      <TextField
                        {...startProps}
                        inputProps={inputProps}
                        value={`${startValue || "DD/MM/YYYY"} - ${
                          endProps.inputProps.value || "DD/MM/YYYY"
                        }`}
                        fullWidth
                        size="small"
                      />
                    );
                  }}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={12} sm={3.5}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DateRangePicker
                  startText="Order Start Date - End Date"
                  endText="To Date"
                  inputFormat="dd/MM/yyyy"
                  value={tempOrderDateRange}
                  onChange={handleOrderDateChange}
                  renderInput={({ inputProps, ...startProps }, endProps) => {
                    const startValue = inputProps.value;
                    delete inputProps.value;
                    return (
                      <TextField
                        {...startProps}
                        inputProps={inputProps}
                        value={`${startValue || "DD/MM/YYYY"} - ${
                          endProps.inputProps.value || "DD/MM/YYYY"
                        }`}
                        fullWidth
                        size="small"
                      />
                    );
                  }}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={tempFilters.liveOrderStatus}
                  onChange={(e) =>
                    handleTempFilterChange("liveOrderStatus", e.target.value)
                  }
                  label="Status"
                  size="small"
                >
                  <MenuItem value="">All</MenuItem>
                  {statusOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                placeholder="Price Type"
                size="small"
                value={tempFilters.priceType}
                onChange={(e) =>
                  handleTempFilterChange("priceType", e.target.value)
                }
              />
            </Grid>
            <Grid item xs={12} md={4} container spacing={1}>
              <Grid item xs={7}>
                <Button
                  variant="contained"
                  onClick={applyFilters}
                  fullWidth
                  data-testid="apply-filters-btn"
                >
                  Apply Filters
                </Button>
              </Grid>
              <Grid item xs={5}>
                <Button
                  variant="outlined"
                  onClick={clearAll}
                  startIcon={<RefreshIcon />}
                  fullWidth
                >
                  Clear
                </Button>
              </Grid>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Orders Table */}
      {meeshoData?.payments?.loading ? (
        <Loader />
      ) : (
        <Card>
          <CardContent>
            <CustomTable
              columns={columns}
              rows={orders}
              page={page}
              rowsPerPage={rowsPerPage}
              count={meeshoData?.payments?.total || 0}
              Order={order}
              OrderBy={orderBy}
              sortData={sortData}
              handleChangePage={handleChangePage}
              handleChangeRowsPerPage={handleChangeRowsPerPage}
              loading={meeshoData?.payments?.loading}
              showTotalInFooter={true}
            />
          </CardContent>
        </Card>
      )}

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
      >
        <MenuItem
          onClick={() => {
            setAnchorEl(null);
            if (selectedOrder?._id) {
              setOrderDetails(selectedOrder);
              setDetailsDialog(true);
            }
          }}
        >
          <ViewIcon sx={{ mr: 1 }} />
          View Details
        </MenuItem>
      </Menu>

      <Dialog
        open={statusDialog}
        onClose={() => setStatusDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {selectedOrders.length > 1
            ? "Bulk Update Status"
            : "Update Order Status"}
        </DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>New Status</InputLabel>
            <Select
              value={newStatus}
              onChange={(e) => setNewStatus(e.target.value)}
              label="New Status"
            >
              {statusOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            fullWidth
            label="Reason (Optional)"
            value={statusReason}
            onChange={(e) => setStatusReason(e.target.value)}
            sx={{ mt: 2 }}
            multiline
            rows={3}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStatusDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleBulkStatusUpdate}
            disabled={!newStatus}
          >
            Update
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={detailsDialog}
        onClose={() => setDetailsDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Payment Details</DialogTitle>
        <DialogContent dividers>
          {orderDetails && (
            <Box>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 2 }}>
                    <Typography variant="subtitle2">Sub Order No</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {orderDetails.subOrderNo}
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 2 }}>
                    <Typography variant="subtitle2">Order Date</Typography>
                    <Typography variant="body1">
                      {moment.utc(orderDetails?.orderDate).format("DD/MM/YYYY")}
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 2 }}>
                    <Typography variant="subtitle2">Payment Date</Typography>
                    <Typography variant="body1">
                      {moment
                        .utc(orderDetails?.paymentDate)
                        .format("DD/MM/YYYY")}
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 2 }}>
                    <Typography variant="subtitle2">Price Type</Typography>
                    <Typography variant="body1">
                      {orderDetails.priceType}
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12}>
                  <Paper sx={{ p: 2 }}>
                    <Typography variant="subtitle2">Product Name</Typography>
                    <Typography variant="body1">
                      {orderDetails.productName}
                    </Typography>
                  </Paper>
                </Grid>

                {/* Core Financial Info */}
                <Grid item xs={12} md={4}>
                  <Paper sx={{ p: 2 }}>
                    <Typography variant="subtitle2">Listing Price</Typography>
                    <Typography variant="body1">
                      {formatCurrency(orderDetails.listingPrice)}
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Paper sx={{ p: 2 }}>
                    <Typography variant="subtitle2">
                      Total Sale Amount
                    </Typography>
                    <Typography variant="body1">
                      {formatCurrency(orderDetails.totalSaleAmount)}
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Paper sx={{ p: 2 }}>
                    <Typography variant="subtitle2">
                      Sale Return Amount
                    </Typography>
                    <Typography variant="body1" color="error">
                      {formatCurrency(orderDetails.totalSaleReturnAmount)}
                    </Typography>
                  </Paper>
                </Grid>

                {/* Quantity and Tax Info */}
                <Grid item xs={12} md={4}>
                  <Paper sx={{ p: 2 }}>
                    <Typography variant="subtitle2">Quantity</Typography>
                    <Typography variant="body1">
                      {orderDetails.quantity}
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Paper sx={{ p: 2 }}>
                    <Typography variant="subtitle2">GST %</Typography>
                    <Typography variant="body1">
                      {orderDetails.productGstPercent}%
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Paper sx={{ p: 2 }}>
                    <Typography variant="subtitle2">TDS Rate</Typography>
                    <Typography variant="body1">
                      {orderDetails.tdsRate}%
                    </Typography>
                  </Paper>
                </Grid>

                {/* Settlement Info */}
                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 2 }}>
                    <Typography variant="subtitle2">
                      Final Settlement
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {formatCurrency(orderDetails.finalSettlementAmount)}
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 2 }}>
                    <Typography variant="subtitle2">Transaction ID</Typography>
                    <Typography variant="body1">
                      {orderDetails.transactionId || "-"}
                    </Typography>
                  </Paper>
                </Grid>

                {/* Commission & Fee Breakdown */}
                <Grid item xs={12}>
                  <Typography variant="h6" sx={{ mt: 3 }}>
                    Fees & Deductions
                  </Typography>
                </Grid>

                <Grid item xs={12} md={4}>
                  <Paper sx={{ p: 2 }}>
                    <Typography variant="subtitle2">
                      Meesho Commission
                    </Typography>
                    <Typography variant="body1">
                      {formatCurrency(orderDetails.meeshoCommission)}
                    </Typography>
                  </Paper>
                </Grid>

                <Grid item xs={12} md={4}>
                  <Paper sx={{ p: 2 }}>
                    <Typography variant="subtitle2">Warehousing Fee</Typography>
                    <Typography variant="body1">
                      {formatCurrency(orderDetails.warehousingFee)}
                    </Typography>
                  </Paper>
                </Grid>

                <Grid item xs={12} md={4}>
                  <Paper sx={{ p: 2 }}>
                    <Typography variant="subtitle2">Shipping Charge</Typography>
                    <Typography variant="body1">
                      {formatCurrency(orderDetails.shippingCharge)}
                    </Typography>
                  </Paper>
                </Grid>

                <Grid item xs={12} md={4}>
                  <Paper sx={{ p: 2 }}>
                    <Typography variant="subtitle2">Return Premium</Typography>
                    <Typography variant="body1">
                      {formatCurrency(orderDetails.returnPremium)}
                    </Typography>
                  </Paper>
                </Grid>

                <Grid item xs={12} md={4}>
                  <Paper sx={{ p: 2 }}>
                    <Typography variant="subtitle2">Claims</Typography>
                    <Typography variant="body1">
                      {formatCurrency(orderDetails.claims)}
                    </Typography>
                  </Paper>
                </Grid>

                <Grid item xs={12} md={4}>
                  <Paper sx={{ p: 2 }}>
                    <Typography variant="subtitle2">Recovery</Typography>
                    <Typography variant="body1">
                      {formatCurrency(orderDetails.recovery)}
                    </Typography>
                  </Paper>
                </Grid>

                {/* Remarks / Reasons */}
                {(orderDetails.compensationReason ||
                  orderDetails.claimsReason ||
                  orderDetails.recoveryReason) && (
                  <>
                    <Grid item xs={12}>
                      <Typography variant="h6" sx={{ mt: 3 }}>
                        Remarks
                      </Typography>
                    </Grid>

                    {orderDetails.compensationReason && (
                      <Grid item xs={12}>
                        <Paper sx={{ p: 2 }}>
                          <Typography variant="subtitle2">
                            Compensation Reason
                          </Typography>
                          <Typography variant="body1">
                            {orderDetails.compensationReason}
                          </Typography>
                        </Paper>
                      </Grid>
                    )}
                    {orderDetails.claimsReason && (
                      <Grid item xs={12}>
                        <Paper sx={{ p: 2 }}>
                          <Typography variant="subtitle2">
                            Claim Reason
                          </Typography>
                          <Typography variant="body1">
                            {orderDetails.claimsReason}
                          </Typography>
                        </Paper>
                      </Grid>
                    )}
                    {orderDetails.recoveryReason && (
                      <Grid item xs={12}>
                        <Paper sx={{ p: 2 }}>
                          <Typography variant="subtitle2">
                            Recovery Reason
                          </Typography>
                          <Typography variant="body1">
                            {orderDetails.recoveryReason}
                          </Typography>
                        </Paper>
                      </Grid>
                    )}
                  </>
                )}
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PaymentList;
